import { useRef, useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { compressImage } from "../../lib/imageCompression";
import { openAuthFile } from "../../lib/fileOpen";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Pill } from "../hs/hsUtils";
import { TONE_CLASSES } from "../assets/assetUtils";
import { err, fmt, AuthImage, Field } from "./mpUtils";
import { Images, FileText, Trash, UploadSimple, SealCheck } from "@phosphor-icons/react";

/** Work photos + insurance / accreditation documents with expiry dates. */
export function ListingFiles({ contractor, meta, onChanged, readOnly = false }) {
  const photoRef = useRef(null);
  const docRef = useRef(null);
  const [doc, setDoc] = useState({ kind: "public_liability", expiry: "", name: "" });
  const [busy, setBusy] = useState("");
  const upload = async (url, fd, label) => {
    setBusy(label);
    try { const { data } = await API.post(url, fd); toast.success(data.message); onChanged(); } catch (e) { err(e, `Couldn't upload the ${label}`); } finally { setBusy(""); }
  };
  const addPhoto = async (e) => {
    const raw = e.target.files?.[0]; e.target.value = "";
    if (!raw) return;
    const blob = await compressImage(raw, { maxDim: 1800, quality: 0.85 }).catch(() => raw);
    const fd = new FormData();
    fd.append("file", new File([blob], raw.name || "photo.jpg", { type: blob.type || "image/jpeg" }));
    await upload("/marketplace/me/photos", fd, "photo");
  };
  const addDoc = async (e) => {
    const raw = e.target.files?.[0]; e.target.value = "";
    if (!raw) return;
    const fd = new FormData();
    fd.append("file", raw); fd.append("kind", doc.kind); fd.append("expiry", doc.expiry); fd.append("name", doc.name);
    await upload("/marketplace/me/documents", fd, "document");
    setDoc((d) => ({ ...d, expiry: "", name: "" }));
  };
  const remove = async (kind, id) => {
    try { await API.delete(`/marketplace/me/${kind}/${id}`); onChanged(); } catch (e) { err(e, "Couldn't remove it"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="listing-photos">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm inline-flex items-center gap-1.5"><Images size={16} /> Work photos <span className="text-muted-foreground font-normal">({contractor.photos?.length || 0}/12)</span></h3>
          {!readOnly && <><input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={addPhoto} data-testid="photo-file-input" />
            <Button size="sm" variant="outline" onClick={() => photoRef.current?.click()} disabled={busy === "photo"} data-testid="add-photo-btn"><UploadSimple size={14} className="mr-1" /> {busy === "photo" ? "Uploading…" : "Add photo"}</Button></>}
        </div>
        {contractor.photos?.length ? (
          <div className="grid grid-cols-3 gap-2">
            {contractor.photos.map((p) => (
              <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                <AuthImage url={p.url} alt={p.name} className="h-28 w-full rounded-md object-cover border border-border" />
                {!readOnly && <button onClick={() => remove("photos", p.id)} className="absolute top-1 right-1 rounded-md bg-background/90 p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`photo-delete-${p.id}`}><Trash size={13} className="text-rose-600" /></button>}
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground">Photos of finished work get noticeably more enquiries.</p>}
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="listing-documents">
        <h3 className="font-semibold text-sm inline-flex items-center gap-1.5"><FileText size={16} /> Insurance & accreditations</h3>
        <ul className="divide-y divide-border">
          {(contractor.documents || []).map((d) => (
            <li key={d.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`doc-${d.id}`}>
              <div className="flex-1 min-w-0">
                <button className="font-medium hover:underline text-left" onClick={() => openAuthFile(`/marketplace/files/${contractor.id}/${d.id}`)} data-testid={`doc-open-${d.id}`}>{d.label}</button>
                <p className="text-xs text-muted-foreground truncate">{d.name}{d.expiry ? ` · expires ${fmt(d.expiry)}` : " · no expiry"}</p>
              </div>
              {d.verified && <Pill tone={TONE_CLASSES.ok} testid={`doc-verified-${d.id}`}><SealCheck size={11} className="mr-1" /> Verified</Pill>}
              {d.expired && <Pill tone={TONE_CLASSES.overdue} testid={`doc-expired-${d.id}`}>Expired</Pill>}
              {!readOnly && <button onClick={() => remove("documents", d.id)} data-testid={`doc-delete-${d.id}`}><Trash size={14} className="text-muted-foreground hover:text-rose-600" /></button>}
            </li>
          ))}
          {!contractor.documents?.length && <li className="py-2 text-xs text-muted-foreground">A current public liability certificate is required before your listing can be approved.</li>}
        </ul>
        {!readOnly && (
          <div className="grid sm:grid-cols-3 gap-2 pt-2 border-t border-border">
            <Field label="Type">
              <select value={doc.kind} onChange={(e) => setDoc({ ...doc, kind: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="doc-kind-select">
                {Object.entries(meta?.doc_kinds || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Expiry"><Input type="date" value={doc.expiry} onChange={(e) => setDoc({ ...doc, expiry: e.target.value })} data-testid="doc-expiry-input" /></Field>
            <Field label="Label (optional)"><Input value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} placeholder="e.g. PL £5m" data-testid="doc-name-input" /></Field>
            <input ref={docRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={addDoc} data-testid="doc-file-input" />
            <Button size="sm" variant="outline" className="sm:col-span-3" onClick={() => docRef.current?.click()} disabled={busy === "document"} data-testid="add-doc-btn"><UploadSimple size={14} className="mr-1" /> {busy === "document" ? "Uploading…" : "Upload PDF or photo"}</Button>
          </div>
        )}
      </section>
    </div>
  );
}
